import React, { useCallback, useEffect, useState } from "react";
import { Star, MapPin, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Carosello testimonianze: 3 card visibili su desktop, 2 su tablet, 1 su mobile.
 * Prev/next controls + dots indicatori + autoplay opzionale.
 *
 * Props:
 *  - fallback: array di testimonianze da mostrare se l'API non ha ancora dati
 *  - autoplayMs: intervallo autoplay (0 = off). Default 6000.
 */
const TestimonialsCarousel = ({ fallback = [], autoplayMs = 6000 }) => {
  const [items, setItems] = useState(fallback);
  const [start, setStart] = useState(0);
  const [perView, setPerView] = useState(3);
  const [paused, setPaused] = useState(false);

  // Fetch delle testimonianze dal backend
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/testimonials`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => {
        if (cancelled) return;
        const data = Array.isArray(json?.data) ? json.data : [];
        if (data.length > 0) setItems(data);
      })
      .catch(() => {
        /* silenzioso: usa il fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Calcola perView in base alla larghezza
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const total = items.length;
  const canPaginate = total > perView;

  const next = useCallback(() => {
    if (!canPaginate) return;
    setStart((s) => (s + 1) % total);
  }, [canPaginate, total]);

  const prev = useCallback(() => {
    if (!canPaginate) return;
    setStart((s) => (s - 1 + total) % total);
  }, [canPaginate, total]);

  // Autoplay
  useEffect(() => {
    if (!autoplayMs || paused || !canPaginate) return;
    const t = setInterval(next, autoplayMs);
    return () => clearInterval(t);
  }, [autoplayMs, paused, canPaginate, next]);

  if (total === 0) return null;

  // Costruisce le card visibili wrap-around
  const visible = [];
  for (let i = 0; i < Math.min(perView, total); i++) {
    visible.push(items[(start + i) % total]);
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Grid delle card visibili */}
      <div
        className={`grid gap-6 transition-opacity duration-300 ${
          perView === 3
            ? "grid-cols-3"
            : perView === 2
            ? "grid-cols-2"
            : "grid-cols-1"
        }`}
      >
        {visible.map((t, i) => (
          <article
            key={t.id + "-" + i}
            className="group relative bg-white p-7 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-[0_20px_40px_-15px_rgba(10,31,68,0.15)] transition-all duration-500 flex flex-col"
          >
            {/* Quote decorativa in alto a destra */}
            <Quote
              className="absolute top-5 right-5 w-8 h-8 text-[#F4C542]/15 group-hover:text-[#F4C542]/30 transition-colors"
              aria-hidden
            />

            {/* Stelle */}
            <div className="flex items-center gap-0.5 mb-4">
              {Array.from({ length: 5 }).map((_, s) => (
                <Star
                  key={s}
                  className={`w-4 h-4 ${
                    s < (t.rating || 5)
                      ? "fill-[#F4C542] text-[#F4C542]"
                      : "text-gray-200"
                  }`}
                />
              ))}
            </div>

            {/* Titolo (recensione) */}
            {t.role && (
              <h3 className="text-base sm:text-lg font-montserrat font-bold text-[#0A1F44] leading-snug mb-3 pr-6">
                {t.role}
              </h3>
            )}

            {/* Testo */}
            <p className="text-sm text-gray-700 mb-6 leading-relaxed italic flex-1">
              "{t.text}"
            </p>

            {/* Autore */}
            <div className="border-t border-gray-100 pt-5">
              <p className="text-sm text-[#0A1F44] mb-1 font-montserrat font-bold">
                {t.name}
              </p>
              {t.location && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />
                  <span>{t.location}</span>
                </div>
              )}
              {t.date && (
                <p className="text-xs text-[#0FB36B] mt-2 font-semibold">
                  Installato: {t.date}
                </p>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* Controlli */}
      {canPaginate && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Precedente"
            className="hidden sm:flex absolute top-1/2 -left-4 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 hover:border-[#0A1F44] shadow-lg items-center justify-center text-[#0A1F44] transition-all hover:scale-105"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Successiva"
            className="hidden sm:flex absolute top-1/2 -right-4 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 hover:border-[#0A1F44] shadow-lg items-center justify-center text-[#0A1F44] transition-all hover:scale-105"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStart(i)}
                aria-label={`Vai a testimonianza ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === start
                    ? "w-8 bg-[#0A1F44]"
                    : "w-1.5 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          {/* Controlli mobile in basso */}
          <div className="flex sm:hidden items-center justify-center gap-2 mt-4">
            <button
              type="button"
              onClick={prev}
              aria-label="Precedente"
              className="w-9 h-9 rounded-full bg-white border border-gray-200 text-[#0A1F44] flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Successiva"
              className="w-9 h-9 rounded-full bg-white border border-gray-200 text-[#0A1F44] flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TestimonialsCarousel;
